import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../ui/table";
import { ScenarioHeader } from "../../../../components/portfolio-risk-concentration/ScenarioHeader";
import { ScenarioSpinner } from "../../../../components/portfolio-risk-concentration/ScenarioSpinner";
import { ScenarioAsOfPicker, asOfNote } from "../../../../components/portfolio-risk-concentration/ScenarioAsOfPicker";
import { thr, usd, usdSigned } from "../../../../components/portfolio-risk-concentration/format";
import { FILTER_TRIGGER, FilterField } from "../../../../components/portfolio-risk-concentration/PrcFilterBar";
import { downloadCsv } from "../../../../utils/portfolio-risk-concentration/csv";
import {
  UI_DEFAULT_VALUE_BASIS,
  type ValueBasis,
} from "../../../../utils/portfolio-risk-concentration/api/concentration";
import {
  apiDetail,
  downloadScenarioSnapshot,
  scenarioSignature,
  SNAPSHOT_PAGE_SIZE,
  useScenarioMonitorAnswer,
  useScenarioSnapshotPage,
  type ScenarioLimitRow,
  type ScenarioSnapshotFacility,
  type ScenarioSnapshotKind,
  type ScenarioSnapshotRow,
} from "../../../../utils/portfolio-risk-concentration/api/scenario";
import { BASELINE_FACILITIES, useFacilities } from "../../../../utils/portfolio-risk-concentration/api/facilities";
import type { ScenarioDraft } from "../ScenarioSimulatorPage";
import { scenarioBatchInput } from "./scenarioInput";

// See ScenarioMonitorStep's identical constant: all three bases are real; FMV renders blank until the pipeline derives fmv_final.
const MEASURED_IN_OPTIONS: { value: ValueBasis; label: string }[] = [
  { value: "net_investment", label: "Book Value" },
  { value: "present_value", label: "Present Value ( DCB)" },
  { value: "fair_market_value", label: "Fair Market Value (FMV)" },
];

// The Snapshot page's Facility control: both books together, or one. (The two contract tables and the summary's columns follow it.)
const FACILITY_OPTIONS: { value: ScenarioSnapshotFacility; label: string }[] = [
  { value: "ALL", label: "All Facilities" },
  { value: "CAP1", label: "CAP 1" },
  { value: "CAP2", label: "CAP 2" },
];

// ── Portfolio Concentration Summary: the scenario's risk limits, CAP 1 and CAP 2 side by side ─────────────────────────────
interface SummaryRow {
  category: string;
  limit: string;
  cap1?: ScenarioLimitRow;
  cap2?: ScenarioLimitRow;
}

/** One row per limit with each book's figures beside it, in the order the server listed them (the Snapshot page's table shape). */
function pivotSummary(cap1: ScenarioLimitRow[], cap2: ScenarioLimitRow[]): SummaryRow[] {
  const byLimit = new Map<string, SummaryRow>();
  const add = (rows: ScenarioLimitRow[], book: "cap1" | "cap2") => {
    for (const r of rows) {
      const key = `${r.categoryGroup}\u0000${r.limitDesc}`;
      const row = byLimit.get(key) ?? { category: r.categoryGroup, limit: r.limitDesc };
      row[book] = r;
      byLimit.set(key, row);
    }
  };
  add(cap1, "cap1");
  add(cap2, "cap2");
  return [...byLimit.values()];
}

/** Threshold minus current, in points of the book (positive: under the limit), like the Snapshot's Over/(Under); null when not measured. */
function overUnder(r: ScenarioLimitRow | undefined): number | null {
  if (!r || !r.measured || r.currentDollarPct === null) return null;
  return Number((r.thresholdPct - r.currentDollarPct).toFixed(2));
}

function summaryCells(r: ScenarioLimitRow | undefined): string[] {
  const measured = !!r && r.measured && r.currentDollarPct !== null;
  return [
    r ? thr(r.thresholdPct) : "—",
    measured ? thr(r!.currentDollarPct) : "—",
    measured ? usd(r!.currentDollars ?? null) : "—",
    thr(overUnder(r)),
    // dollars left to the limit (or, with a minus, already past it): the Capacity page's Headroom, not worked out again here
    measured ? usdSigned(r!.headroomDollars ?? null) : "—",
  ];
}

/** The same five figures as plain numbers for the CSV (percentages in points of the book: 3.5 for 3.50%; dollars without the sign),
 *  nothing for a figure the limit does not have, so a spreadsheet can sum and sort them. */
function summaryValues(r: ScenarioLimitRow | undefined): (number | null)[] {
  const measured = !!r && r.measured && r.currentDollarPct !== null;
  return [
    r ? r.thresholdPct : null,
    measured ? r!.currentDollarPct : null,
    measured ? (r!.currentDollars ?? null) : null,
    overUnder(r),
    measured ? (r!.headroomDollars ?? null) : null,
  ];
}

const BOOK_COLUMNS = (label: string) => [
  `${label} Limit`,
  `${label} Current Concentration`,
  `${label} Concentration $`,
  `${label} Over/(Under)`,
  `${label} Headroom`,
];

/** How many figures each book shows (BOOK_COLUMNS, summaryCells). */
const BOOK_COLUMN_COUNT = 5;

// ── The two contract tables ──────────────────────────────────────────────────────────────────────────
const INTEGER_KEYS = new Set(["remainingMonthsCash", "daysDelinquent"]);
const MONEY_KEYS = new Set(["netInvestment", "dcb"]);

function cellText(key: string, value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  if (MONEY_KEYS.has(key)) return Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
  if (INTEGER_KEYS.has(key)) return Number(value).toLocaleString("en-US", { maximumFractionDigits: 0 });
  return String(value);
}

function Panel({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <p className="text-sm font-semibold">{title}</p>
        {right}
      </div>
      {children}
    </div>
  );
}

/** One of the Snapshot's contract tables for this scenario: a page of rows at a time from the server, a contract-number search, and the
 *  whole table as a CSV (streamed by the server). */
function ContractsPanel({
  kind,
  title,
  draft,
  facility,
  buildInput,
}: {
  kind: ScenarioSnapshotKind;
  title: string;
  draft: ScenarioDraft;
  facility: ScenarioSnapshotFacility;
  buildInput: (() => ReturnType<typeof scenarioBatchInput>) | null;
}) {
  const [offset, setOffset] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // the search applies a moment after typing stops; either way the first page of the new result is the one shown
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setOffset(0);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);
  useEffect(() => setOffset(0), [facility, draft.asOfDate]);

  const q = useScenarioSnapshotPage(kind, scenarioSignature(draft), draft.asOfDate, facility, search, offset, buildInput);
  const page = q.data ?? null;
  const total = page?.total ?? 0;
  const from = total === 0 ? 0 : (page?.offset ?? 0) + 1;
  const to = Math.min((page?.offset ?? 0) + SNAPSHOT_PAGE_SIZE, total);

  async function download() {
    if (!buildInput) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadScenarioSnapshot(kind, buildInput(), facility);
    } catch (err) {
      setDownloadError(apiDetail(err, "Could not download the file."));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Panel
      title={page ? `${title} — ${page.asOfDate}` : title}
      right={
        <div className="flex items-center gap-2">
          <Input
            aria-label={`Search ${title}`}
            className="h-8 w-52 text-xs"
            placeholder={page ? `Search ${total.toLocaleString("en-US")} records…` : "Search contract number…"}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button variant="outline" size="sm" className="h-8" disabled={!buildInput || downloading || !page} onClick={() => void download()}>
            {downloading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
            {downloading ? "Preparing download…" : "Download CSV"}
          </Button>
        </div>
      }
    >
      {downloadError && <p className="px-4 pt-2 text-[11px] text-destructive">{downloadError}</p>}
      {!page && q.isFetching ? (
        <ScenarioSpinner label={`Loading ${title}…`} />
      ) : !page && q.isError ? (
        <p role="alert" className="px-4 py-6 text-sm text-destructive">
          Could not load {title}: {apiDetail(q.error, "try again.")}
        </p>
      ) : page ? (
        <div className={q.isFetching ? "opacity-60" : undefined}>
          {page.notes.map((note) => (
            <p key={note} className="px-4 pt-2 text-[11px] text-muted-foreground">
              {note}
            </p>
          ))}
          <div className="max-h-[32rem] overflow-auto">
            <Table>
              <TableHeader className="bg-muted">
                <TableRow>
                  {page.columns.map((c) => (
                    <TableHead key={c.key} className={INTEGER_KEYS.has(c.key) || MONEY_KEYS.has(c.key) ? "text-right" : undefined}>
                      {c.header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {page.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={page.columns.length} className="py-6 text-center text-sm text-muted-foreground">
                      {search ? "No contract matches that search." : "No contracts."}
                    </TableCell>
                  </TableRow>
                )}
                {page.rows.map((row: ScenarioSnapshotRow, i) => (
                  <TableRow key={`${row.contractNumber}-${row.scheduleNumber}-${i}`}>
                    {page.columns.map((c) => (
                      <TableCell
                        key={c.key}
                        className={INTEGER_KEYS.has(c.key) || MONEY_KEYS.has(c.key) ? "text-right tabular-nums" : "whitespace-nowrap"}
                      >
                        {cellText(c.key, row[c.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
            <span>{total === 0 ? "0 of 0" : `${from.toLocaleString("en-US")}–${to.toLocaleString("en-US")} of ${total.toLocaleString("en-US")}`}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={offset === 0 || q.isFetching} onClick={() => setOffset((o) => Math.max(0, o - SNAPSHOT_PAGE_SIZE))}>
                <ChevronLeft className="mr-1 h-3.5 w-3.5" />
                Prev
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" disabled={offset + SNAPSHOT_PAGE_SIZE >= total || q.isFetching} onClick={() => setOffset((o) => o + SNAPSHOT_PAGE_SIZE)}>
                Next
                <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Panel>
  );
}

/** 4 · Scenario Snapshot — the Snapshot page's three tables for this scenario: the risk limits (Portfolio Concentration Summary), the
 *  contracts they were measured on (PRC Contract Detail) and the contracts they leave out (PRC Ineligible Contracts). Everything is the
 *  scenario's: the effective date's snapshot with the contracts the waterfall moved shown under their destination facility and code. */
export default function ScenarioSnapshotStep({
  draft,
  patch,
  base,
}: {
  draft: ScenarioDraft;
  patch: (p: Partial<ScenarioDraft>) => void;
  base: string;
}) {
  const navigate = useNavigate();
  const facilitiesQ = useFacilities();

  // The filters start where they were left (kept on the draft), so leaving this step and coming back shows the same choices.
  const view = draft.snapshotView;
  const [facility, setFacility] = useState<ScenarioSnapshotFacility>((view?.facility as ScenarioSnapshotFacility | undefined) ?? "ALL");
  const [measuredIn, setMeasuredIn] = useState<ValueBasis>(view?.measuredIn ?? UI_DEFAULT_VALUE_BASIS);
  useEffect(() => {
    patch({ snapshotView: { facility, measuredIn } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facility, measuredIn]);
  const [limitSearch, setLimitSearch] = useState("");

  useEffect(() => {
    if (!draft.waterfall) navigate(`${base}/apply`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The scenario as the server reads it, for the two contract tables and their downloads (null until a waterfall exists).
  const buildInput = draft.waterfall ? () => scenarioBatchInput(draft, facilitiesQ.data ?? BASELINE_FACILITIES) : null;

  // The risk limits are the Monitor's own answer for this scenario, date and basis (asked once and kept: coming here from Monitor with the
  // same Measured in is no request at all).
  const answer = useScenarioMonitorAnswer(
    scenarioSignature(draft),
    draft.asOfDate,
    undefined,
    measuredIn,
    buildInput ? () => ({ ...buildInput(), valueBasis: measuredIn }) : null,
  );
  const result = answer.data ?? null;
  const loading = answer.isFetching;

  const summary = useMemo(
    () => pivotSummary(result?.rows.cap1 ?? [], result?.rows.cap2 ?? []),
    [result],
  );
  const shownRows = useMemo(() => {
    const q = limitSearch.trim().toLowerCase();
    return q ? summary.filter((r) => `${r.category} ${r.limit}`.toLowerCase().includes(q)) : summary;
  }, [summary, limitSearch]);
  const books = facility === "CAP1" ? (["cap1"] as const) : facility === "CAP2" ? (["cap2"] as const) : (["cap1", "cap2"] as const);
  const bookLabel = { cap1: "CAP 1", cap2: "CAP 2" } as const;

  function downloadSummaryCsv() {
    downloadCsv(
      `scenario-risk-limits-${result?.asOfDate ?? "export"}.csv`,
      ["Category", "Limit Description", ...books.flatMap((b) => BOOK_COLUMNS(bookLabel[b]))],
      shownRows.map((r) => [r.category, r.limit, ...books.flatMap((b) => summaryValues(r[b]))]),
    );
  }

  return (
    <div>
      <ScenarioHeader
        title="Scenario Snapshot"
        subtitle={`${draft.approvedScenarioLabel ? `Approved scenario ${draft.approvedScenarioLabel} · ` : ""}The Snapshot's risk limits and contract lists, under this session's assignments.`}
        notReal
      />

      {/* Same Back/Next pair as the other steps, at the top so a long table doesn't make stepping mean scrolling to the bottom. */}
      <div className="mt-3 flex justify-between">
        <Button variant="outline" onClick={() => navigate(`${base}/monitor`)}>
          ← Scenario Monitor
        </Button>
        <Button disabled={!result} onClick={() => navigate(`${base}/propose`)}>
          Propose →
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <FilterField label="Facility">
          <Select value={facility} onValueChange={(v) => setFacility(v as ScenarioSnapshotFacility)}>
            <SelectTrigger className={FILTER_TRIGGER} aria-label="Facility">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FACILITY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Measured in">
          <Select value={measuredIn} onValueChange={(v) => setMeasuredIn(v as ValueBasis)} disabled={loading}>
            <SelectTrigger className={FILTER_TRIGGER} aria-label="Measured in">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEASURED_IN_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        {/* The effective date, the same one as on Scenario Monitor; every table here is read on it. */}
        <div className="ml-auto">
          <ScenarioAsOfPicker value={draft.asOfDate} onChange={(asOfDate) => patch({ asOfDate })} disabled={loading} />
        </div>
      </div>
      {!loading && result && asOfNote(draft.asOfDate, result.asOfDate) && (
        <p className="mt-1 text-[11px] text-muted-foreground">{asOfNote(draft.asOfDate, result.asOfDate)}</p>
      )}
      {measuredIn === "fair_market_value" && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          Fair market value isn't populated by the pipeline yet, so every figure in the limits table renders blank until it is.
        </p>
      )}

      <Panel
        title={result ? `Portfolio Concentration Summary — ${result.asOfDate}` : "Portfolio Concentration Summary"}
        right={
          <div className="flex items-center gap-2">
            <Input
              aria-label="Search Portfolio Concentration Summary"
              className="h-8 w-52 text-xs"
              placeholder={`Search ${summary.length} records…`}
              value={limitSearch}
              onChange={(e) => setLimitSearch(e.target.value)}
            />
            <Button variant="outline" size="sm" className="h-8" disabled={loading || shownRows.length === 0} onClick={downloadSummaryCsv}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Download CSV
            </Button>
          </div>
        }
      >
        {!result && answer.isFetching ? (
          <ScenarioSpinner label="Computing scenario…" />
        ) : !result && answer.isError ? (
          <p role="alert" className="px-4 py-6 text-sm text-destructive">
            Could not compute this scenario as of {draft.asOfDate}. Pick another date, or go back to the Scenario step and try again.
          </p>
        ) : result ? (
          <div className={loading ? "opacity-60" : undefined}>
            <div className="max-h-[32rem] overflow-auto">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Limit Description</TableHead>
                    {books.flatMap((b) =>
                      BOOK_COLUMNS(bookLabel[b]).map((h) => (
                        <TableHead key={`${b}-${h}`} className="text-right">
                          {h}
                        </TableHead>
                      )),
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shownRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2 + books.length * BOOK_COLUMN_COUNT} className="py-6 text-center text-sm text-muted-foreground">
                        {limitSearch ? "No limit matches that search." : "No limits."}
                      </TableCell>
                    </TableRow>
                  )}
                  {shownRows.map((r) => (
                    <TableRow key={`${r.category}-${r.limit}`}>
                      <TableCell className="whitespace-nowrap">{r.category}</TableCell>
                      <TableCell>{r.limit}</TableCell>
                      {books.flatMap((b) =>
                        summaryCells(r[b]).map((text, i) => (
                          <TableCell
                            key={`${b}-${i}`}
                            // a negative headroom (the last figure) is a limit already past: red, like a breach elsewhere
                            className={`text-right tabular-nums ${i === BOOK_COLUMN_COUNT - 1 && text.startsWith("-") ? "text-destructive" : ""}`}
                          >
                            {text}
                          </TableCell>
                        )),
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
      </Panel>

      <ContractsPanel kind="contracts" title="PRC Contract Detail" draft={draft} facility={facility} buildInput={buildInput} />
      <ContractsPanel kind="ineligible" title="PRC Ineligible Contracts" draft={draft} facility={facility} buildInput={buildInput} />

      <div className="mt-4 flex justify-between">
        <Button variant="outline" onClick={() => navigate(`${base}/monitor`)}>
          ← Scenario Monitor
        </Button>
        <Button disabled={!result} onClick={() => navigate(`${base}/propose`)}>
          Propose →
        </Button>
      </div>
    </div>
  );
}
